import {
    Box,
    Button,
    Heading,
    Modal,
    Table,
    TableBody,
    TableCell,
    TableCellSelectRow,
    TableContainer,
    TableHead,
    TableHeader,
    TableHeaderSelectAll,
    TableRow,
} from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import type { IOrganizationGeneralSettings } from '@common/validation/organization';
import { findActiveAgbCode } from '@common/interfaces/agbCode';
import { formatString } from '@common/utils/formatString';
import { getSelectedLanguage } from '@frontend/translate';
import { useAbortableFetch } from '@frontend/utils';
import type { VektisOrganizationSettings } from '../utils/vektisUtils';
import {
    getVektisOrganization,
    isVektisOrganizationInactive,
    mapVektisResponseToOrganization,
} from '../utils/vektisUtils';

const vektisFieldsInOrder: (keyof IOrganizationGeneralSettings)[] = [
    'name',
    'nameStatutory',
    'kvkNumber',
    'phoneNumber',
    'emailAddressRequestInbox',
    'url',
    'street',
    'houseNumber',
    'houseNumberSuffix',
    'zip',
    'city',
    'state',
];

type TransferableVektisOrganizationSettings = Omit<VektisOrganizationSettings, 'agbCodes'>;

type TableRowData = {
    clientSafeValue?: string | null;
    fieldName: keyof TransferableVektisOrganizationSettings;
    isSelected: boolean;
    vektisValue: string | null;
};

type VektisCompareModalProps = {
    isOpen: boolean;
    onAutoFill: (autoFillOrganization: Partial<VektisOrganizationSettings>) => void;
    onClose: () => void;
    organization: IOrganizationGeneralSettings;
};

export function VektisCompareModal(props: VektisCompareModalProps) {
    const translate = getSelectedLanguage();
    const abortableFetch = useAbortableFetch();

    const [searchError, setSearchError] = useState<string | undefined>();
    const [allDataMatches, setAllDataMatches] = useState(false);
    const [tableRows, setTableRows] = useState<TableRowData[]>([]);

    const numSelected = tableRows.filter((row) => row.isSelected).length;
    const areAllRowsSelected = numSelected === tableRows.length;

    const activeAgbCode = findActiveAgbCode(props.organization.agbCodes)?.agbCode;

    useEffect(() => {
        if (props.isOpen) {
            loadVektisData();
        }
    }, [props.isOpen]);

    return (
        <Modal
            isOpen={props.isOpen}
            onClose={() => {
                props.onClose();
            }}
            size="large"
        >
            <Modal.Header>{translate.common.vektis}</Modal.Header>
            <Modal.CloseButton />
            <Modal.Body>
                {renderStatusMessage()}
                {tableRows.length > 0 && (
                    <>
                        <Heading
                            mb={4}
                            size="small"
                        >
                            {formatString(translate.organization.vektisCompare.dataDoesntMatch, [activeAgbCode ?? ''])}
                        </Heading>

                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableHeader>{translate.common.details}</TableHeader>
                                        <TableHeader>{translate.common.clientSafe}</TableHeader>
                                        <TableHeaderSelectAll
                                            checked={numSelected > 0}
                                            indeterminate={numSelected > 0 && numSelected < tableRows.length}
                                            onSelect={handleSelectAll}
                                        />
                                        <TableHeader>{translate.common.vektis}</TableHeader>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {tableRows.map((row: TableRowData) => (
                                        <TableRow key={row.fieldName}>
                                            <TableCell fontWeight="bold">
                                                {translate.organization[row.fieldName]}
                                            </TableCell>
                                            <TableCell>{row.clientSafeValue}</TableCell>
                                            <TableCellSelectRow
                                                isSelected={row.isSelected}
                                                onSelect={() => handleSelectRow(row.fieldName)}
                                            />
                                            <TableCell>{row.vektisValue}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </>
                )}
            </Modal.Body>

            {tableRows.length > 0 && (
                <Modal.Footer>
                    <Button
                        onClick={props.onClose}
                        variant="outline"
                    >
                        {translate.common.cancel}
                    </Button>
                    <Button onClick={handleSubmit}>{translate.organization.vektisCompare.copyDetails}</Button>
                </Modal.Footer>
            )}
        </Modal>
    );

    async function loadVektisData() {
        setAllDataMatches(false);
        setSearchError(undefined);
        setTableRows([]);

        if (!activeAgbCode) {
            setSearchError(translate.organization.vektisCompare.noAgbProvided);

            return;
        }

        const response = await getVektisOrganization(activeAgbCode, abortableFetch);
        if (!response) {
            return setSearchError(translate.organization.vektisCompare.noOrganizationFound);
        }

        if (response?.status !== 200) {
            return handleErrorCode(response?.status);
        }

        if (isVektisOrganizationInactive(response?.json?.organization)) {
            return setSearchError(
                formatString(translate.organization.vektisCompare.inactiveOrganizationError, [activeAgbCode])
            );
        }

        const mappedOrganization = mapVektisResponseToOrganization(response.json);
        buildTableRows(mappedOrganization, props.organization);
    }

    function handleSelectAll() {
        setTableRows((prevRows) => prevRows.map((row) => ({ ...row, isSelected: !areAllRowsSelected })));
    }

    function handleSelectRow(id: keyof VektisOrganizationSettings) {
        setTableRows((prevRows) =>
            prevRows.map((row) => {
                if (row.fieldName === id) {
                    return { ...row, isSelected: !row.isSelected };
                }

                return row;
            })
        );
    }

    function renderStatusMessage() {
        if (searchError || allDataMatches) {
            return (
                <Box
                    mb={8}
                    textAlign={'center'}
                >
                    <Heading size="small">
                        {searchError ? searchError : translate.organization.vektisCompare.allDataMatches}
                    </Heading>
                </Box>
            );
        }
    }

    function handleErrorCode(statusCode: number) {
        if (statusCode === 404) {
            return setSearchError(
                formatString(translate.organization.vektisCompare.noOrganizationFound, [activeAgbCode ?? ''])
            );
        }
        if (statusCode === 400) {
            return setSearchError(
                formatString(translate.organization.vektisCompare.invalidAgbCode, [activeAgbCode ?? ''])
            );
        }
    }

    function buildTableRows(
        mappedVektisOrganization: Partial<VektisOrganizationSettings> | undefined,
        organization: IOrganizationGeneralSettings
    ) {
        if (!mappedVektisOrganization) {
            return;
        }

        const { agbCodes, ...vektisOrganization } = mappedVektisOrganization;

        const newTableRows: TableRowData[] = Object.entries(vektisOrganization)
            .filter(([key, value]) => {
                if (!value) {
                    return false;
                }
                const isValueDifferent =
                    organization[key as keyof TransferableVektisOrganizationSettings]?.toLowerCase() !==
                    value.toLowerCase();

                return isValueDifferent;
            })
            .map(([key, value]) => ({
                clientSafeValue: organization[key as keyof TransferableVektisOrganizationSettings],
                fieldName: key as keyof TransferableVektisOrganizationSettings,
                isSelected: false,
                vektisValue: value,
            }))
            .sort((a, b) => {
                return vektisFieldsInOrder.indexOf(a.fieldName) - vektisFieldsInOrder.indexOf(b.fieldName);
            });

        if (newTableRows.length === 0) {
            setAllDataMatches(true);

            return;
        }

        setTableRows(newTableRows);
    }

    function handleSubmit() {
        const autoFillOrganization: Partial<TransferableVektisOrganizationSettings> = {
            ...tableRows
                .filter((row) => row.isSelected)
                .reduce((acc, row) => {
                    if (row.vektisValue) {
                        acc[row.fieldName] = row.vektisValue;
                    }

                    return acc;
                }, {} as Partial<TransferableVektisOrganizationSettings>),
        };

        props.onAutoFill(autoFillOrganization);
    }
}
